"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Link from "next/link";
import { countryToCurrency } from "@/lib/currency";
import {
  isCreativeDealType,
  requiresFinancingTerms,
  type DealType,
} from "@/lib/dealTypes";
import {
  createEmptyFinancingFields,
  type FinancingFormFields,
} from "@/lib/financingTerms";
import {
  applyTemplateToFormValues,
  buildTemplateDataFromForm,
  type ListingTemplateRow,
} from "@/lib/listingTemplates";
import { createListingTemplate } from "@/lib/listingTemplateClient";
import {
  validateListingForm,
  validateListingFormStep,
  claimListingSubmission,
  focusListingField,
  releaseListingSubmission,
  type ListingFormFieldError,
  type ListingFormImage,
  type ListingFormMode,
  type ListingFormNormalizedPayload,
  type ListingFormStep,
  type ListingFormValues,
  type ListingPublishPhase,
  type PinStatus,
} from "@/lib/listing-form";
import StepLocation from "./StepLocation";
import StepDetails from "./StepDetails";
import StepPhotos from "./StepPhotos";
import StepReview from "./StepReview";
import TemplateControls from "./TemplateControls";
import { listingFormStyles as s } from "./listingFormStyles";
import { theme } from "@/lib/uiTheme";

const STEP_LABELS: Record<ListingFormStep, string> = {
  1: "Location",
  2: "Details",
  3: "Photos",
  4: "Review",
};

export type ListingFormSubmitResult = {
  listingId: string;
  imageWarning?: string | null;
};

type Props = {
  mode: ListingFormMode;
  initialValues: ListingFormValues;
  title: string;
  subtitle: string;
  phase: ListingPublishPhase;
  phaseMessage?: string | null;
  uploadProgress?: { current: number; total: number; fileName: string } | null;
  submitError?: string | null;
  successListingId?: string | null;
  onSubmit: (
    normalized: ListingFormNormalizedPayload,
    values: ListingFormValues,
  ) => Promise<void>;
};

export default function ListingForm({
  mode,
  initialValues,
  title,
  subtitle,
  phase,
  phaseMessage,
  uploadProgress,
  submitError,
  successListingId,
  onSubmit,
}: Props) {
  const [step, setStep] = useState<ListingFormStep>(1);
  const [values, setValues] = useState<ListingFormValues>(initialValues);
  const [stepErrors, setStepErrors] = useState<ListingFormFieldError[]>([]);
  const [reviewErrors, setReviewErrors] = useState<ListingFormFieldError[]>([]);
  const [templates, setTemplates] = useState<ListingTemplateRow[]>([]);
  const focusFieldRef = useRef<string | null>(null);
  const submissionLockRef = useRef(false);
  const busy =
    phase === "validating" ||
    phase === "creating" ||
    phase === "updating" ||
    phase === "uploading_images" ||
    phase === "finalizing_images";

  useEffect(() => {
    const field = focusFieldRef.current;
    if (!field) return;
    const frame = window.requestAnimationFrame(() => focusListingField(field));
    focusFieldRef.current = null;
    return () => window.cancelAnimationFrame(frame);
  }, [step, stepErrors, reviewErrors]);

  function updateField<K extends keyof ListingFormValues>(
    field: K,
    value: ListingFormValues[K],
  ) {
    setValues((prev) => ({ ...prev, [field]: value }));
  }

  function updateFinancingField<K extends keyof FinancingFormFields>(
    field: K,
    value: FinancingFormFields[K],
  ) {
    setValues((prev) => ({
      ...prev,
      financing: { ...prev.financing, [field]: value },
    }));
  }

  function changeDealType(dealType: DealType) {
    const termsWillBeCleared =
      isCreativeDealType(values.deal_type) && !requiresFinancingTerms(dealType);
    if (
      termsWillBeCleared &&
      !window.confirm(
        "Changing this deal type will clear financing terms from this form. This does not update the listing until you save.",
      )
    ) {
      return;
    }
    setValues((prev) => {
      return {
        ...prev,
        deal_type: dealType,
        financing: termsWillBeCleared
          ? createEmptyFinancingFields()
          : prev.financing,
      };
    });
  }

  const replaceTemplates = useCallback(
    (nextTemplates: ListingTemplateRow[]) => {
      setTemplates(nextTemplates);
    },
    [],
  );

  function startFromScratch() {
    if (
      !window.confirm(
        "Clear reusable deal details and financing terms from this form? Property address, price, and photos will remain.",
      )
    )
      return;
    setValues((prev) => ({
      ...prev,
      deal_type: "",
      property_type: "",
      age_restricted: false,
      description: "",
      contact_name: "",
      contact_phone: "",
      contact_email: "",
      deal_constraints: [],
      deal_constraints_notes: "",
      financing: createEmptyFinancingFields(),
    }));
  }

  function applyTemplate(template: ListingTemplateRow) {
    setValues((prev) => {
      const next = applyTemplateToFormValues(prev, template.template_data);
      return { ...prev, ...next };
    });
  }

  async function saveTemplate(
    name: string,
    description: string,
  ): Promise<ListingTemplateRow | null> {
    const templateData = buildTemplateDataFromForm({
      ...values,
      include_market: false,
    });
    if ("error" in templateData) {
      throw new Error(templateData.error);
    }
    return createListingTemplate({
      name,
      description,
      template_data: templateData,
    });
  }

  function onAddressFieldChange(
    field: "address" | "city" | "state" | "zip" | "country",
    value: string,
  ) {
    setValues((prev) => {
      const next: ListingFormValues = { ...prev, [field]: value };
      if (field === "country") {
        if (!prev.currencyOverridden) {
          next.currency = countryToCurrency(value);
        }
        next.state = "";
      }
      // Soft-invalidate pin confirmation without clearing coordinates (preserves focus/map stability).
      if (
        prev.latitude != null &&
        prev.longitude != null &&
        (prev.pinStatus === "geocoded" ||
          prev.pinStatus === "manually_adjusted")
      ) {
        next.pinStatus = "needs_reconfirm";
      }
      return next;
    });
  }

  function onPinChange(next: {
    latitude: number;
    longitude: number;
    pinStatus: PinStatus;
  }) {
    setValues((prev) => ({
      ...prev,
      latitude: next.latitude,
      longitude: next.longitude,
      pinStatus: next.pinStatus,
    }));
  }

  function onPinStatusChange(status: PinStatus) {
    setValues((prev) => ({ ...prev, pinStatus: status }));
  }

  function onImagesChange(images: ListingFormImage[]) {
    setValues((prev) => ({ ...prev, images }));
  }

  function goToStep(next: ListingFormStep, field?: string) {
    if (field) focusFieldRef.current = field;
    setStep(next);
    setStepErrors([]);
    if (field && next === step) {
      window.requestAnimationFrame(() => focusListingField(field));
      focusFieldRef.current = null;
    }
  }

  function handleContinue() {
    const result = validateListingFormStep(values, mode, step);
    if (!result.ok) {
      focusFieldRef.current = result.errors[0]?.field ?? null;
      setStepErrors(result.errors);
      return;
    }
    setStepErrors([]);
    setStep((s) => (s < 4 ? ((s + 1) as ListingFormStep) : s));
  }

  function handleBack() {
    setStepErrors([]);
    setStep((s) => (s > 1 ? ((s - 1) as ListingFormStep) : s));
  }

  async function handleSubmit() {
    if (busy || !claimListingSubmission(submissionLockRef)) return;
    const result = validateListingForm(values, mode);
    setReviewErrors(result.errors);
    if (!result.ok) {
      releaseListingSubmission(submissionLockRef);
      const first = result.errors[0];
      if (first) goToStep(first.step, first.field);
      return;
    }
    try {
      await onSubmit(result.normalized, values);
    } finally {
      releaseListingSubmission(submissionLockRef);
    }
  }

  if (phase === "complete" && successListingId) {
    return (
      <main style={s.page}>
        <div style={s.header}>
          <h1 style={s.title}>
            {mode === "create" ? "Deal published" : "Changes saved"}
          </h1>
          <p style={s.subtitle}>
            {phaseMessage ||
              (mode === "create"
                ? "Your listing is live."
                : "Your listing updates have been saved.")}
          </p>
        </div>
        <div style={s.successBanner}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            <Link
              href={`/listing/${successListingId}`}
              style={{ ...s.btnPrimary, textDecoration: "none" }}
            >
              View listing
            </Link>
            <Link
              href="/my-listings"
              style={{ ...s.btnGhost, textDecoration: "none" }}
            >
              Back to My Listings
            </Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main style={s.page}>
      <div style={s.header}>
        <h1 style={s.title}>{title}</h1>
        <p style={s.subtitle}>{subtitle}</p>
      </div>

      <div style={s.progressWrap} aria-label="Form progress">
        {([1, 2, 3, 4] as ListingFormStep[]).map((n) => (
          <div key={n} style={s.progressStep(step === n, step > n)}>
            Step {n}: {STEP_LABELS[n]}
          </div>
        ))}
      </div>

      {submitError ? (
        <div style={s.errorBanner} role="alert">
          {submitError}
        </div>
      ) : null}

      {(stepErrors.length > 0 || (step === 4 && reviewErrors.length > 0)) ? (
        <div style={s.validationSummary} role="alert" aria-live="assertive">
          <strong>Please fix the following before continuing:</strong>
          <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
            {(step === 4 ? reviewErrors : stepErrors).map((error, index) => (
              <li key={`${error.field}-${index}`}>
                <button
                  type="button"
                  onClick={() => goToStep(error.step, error.field)}
                  style={s.validationLink}
                >
                  {error.message}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {phaseMessage && busy ? (
        <div
          style={{
            marginBottom: 16,
            padding: 12,
            borderRadius: theme.radii.lg,
            background: theme.colors.primaryMuted,
            color: theme.colors.primaryHover,
            fontSize: 14,
          }}
        >
          {phaseMessage}
        </div>
      ) : null}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (step === 4) {
            void handleSubmit();
          } else {
            handleContinue();
          }
        }}
        noValidate
      >
        <TemplateControls
          templates={templates}
          disabled={busy}
          hasPropertyDetails={
            Boolean(values.address.trim()) || values.images.length > 0
          }
          currentDealType={values.deal_type}
          onApply={applyTemplate}
          onSave={saveTemplate}
          onStartFromScratch={startFromScratch}
          onTemplatesChange={replaceTemplates}
        />
        {step === 1 ? (
          <StepLocation
            values={values}
            errors={stepErrors}
            disabled={busy}
            onFieldChange={updateField}
            onAddressFieldChange={onAddressFieldChange}
            onPinChange={onPinChange}
            onPinStatusChange={onPinStatusChange}
          />
        ) : null}
        {step === 2 ? (
          <StepDetails
            values={values}
            errors={stepErrors}
            disabled={busy}
            onFieldChange={updateField}
            onDealTypeChange={changeDealType}
            onFinancingChange={updateFinancingField}
          />
        ) : null}
        {step === 3 ? (
          <StepPhotos
            values={values}
            errors={stepErrors}
            disabled={busy}
            uploadProgress={uploadProgress}
            onFieldChange={updateField}
            onImagesChange={onImagesChange}
          />
        ) : null}
        {step === 4 ? (
          <StepReview
            mode={mode}
            values={values}
            errors={reviewErrors}
            disabled={busy}
            onGoToStep={goToStep}
            onFieldChange={updateField}
          />
        ) : null}

        <div style={s.stickyNav}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {step > 1 ? (
              <button
                type="button"
                onClick={handleBack}
                disabled={busy}
                style={s.btnGhost}
              >
                Back
              </button>
            ) : (
              <Link
                href={mode === "edit" ? "/my-listings" : "/listings"}
                style={{ ...s.btnGhost, textDecoration: "none" }}
              >
                Cancel
              </Link>
            )}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {step < 4 ? (
              <button
                type="submit"
                disabled={busy}
                aria-disabled={busy}
                style={{ ...s.btnPrimary, ...(busy ? s.btnDisabled : null) }}
              >
                Continue
              </button>
            ) : (
              <button
                type="submit"
                disabled={busy}
                aria-disabled={busy}
                style={{ ...s.btnPrimary, ...(busy ? s.btnDisabled : null) }}
              >
                {busy
                  ? phaseMessage ||
                    (mode === "create" ? "Publishing…" : "Saving…")
                  : mode === "create"
                    ? "Publish Deal"
                    : "Save Changes"}
              </button>
            )}
          </div>
        </div>
      </form>
    </main>
  );
}
